import { useEffect, useRef, useState } from 'react';
import maplibregl, { type Map as MapLibreMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { LocationItem } from '../types';

type Props = {
  locations: LocationItem[];
  selectedId?: string;
  onSelect?: (location: LocationItem) => void;
  className?: string;
};

const STYLE_URL = import.meta.env.VITE_OPENFREE_STYLE_URL || 'https://tiles.openfreemap.org/styles/liberty';

function markerColor(score: number) {
  if (score >= 70) return '#2f8f4e';
  if (score >= 55) return '#f59e0b';
  return '#ef553d';
}

export function MapView({ locations, selectedId, onSelect, className = '' }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const markerRefs = useRef<maplibregl.Marker[]>([]);
  const [is3d, setIs3d] = useState(true);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: STYLE_URL,
      center: [106.8272, -6.186],
      zoom: 11.7,
      pitch: 48,
      bearing: -12,
      canvasContextAttributes: {
        antialias: true,
      },
      attributionControl: { compact: true },
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'top-right');
    map.addControl(new maplibregl.GeolocateControl({ positionOptions: { enableHighAccuracy: true }, trackUserLocation: false }), 'top-right');

    map.on('load', async () => {
      try {
        map.addSource('comfort-zones', { type: 'geojson', data: '/data/comfort-zones.geojson' });
        map.addLayer({
          id: 'comfort-zones-fill',
          type: 'fill',
          source: 'comfort-zones',
          paint: {
            'fill-color': [
              'interpolate', ['linear'], ['get', 'score'],
              40, '#ef553d',
              55, '#f59e0b',
              70, '#72b84c',
              90, '#177245',
            ],
            'fill-opacity': 0.3,
          },
        });
        map.addLayer({
          id: 'comfort-zones-outline',
          type: 'line',
          source: 'comfort-zones',
          paint: { 'line-color': '#ffffff', 'line-width': 1.4, 'line-opacity': 0.8 },
        });

        map.addSource('facilities', { type: 'geojson', data: '/data/facilities.geojson' });
        map.addLayer({
          id: 'facilities-points',
          type: 'circle',
          source: 'facilities',
          minzoom: 12,
          paint: {
            'circle-radius': 5,
            'circle-color': '#ffffff',
            'circle-stroke-width': 2,
            'circle-stroke-color': '#0f5b37',
          },
        });
        map.on('click', 'facilities-points', (event) => {
          const feature = event.features?.[0];
          if (!feature || feature.geometry.type !== 'Point') return;
          const coordinates = feature.geometry.coordinates as [number, number];
          const name = String(feature.properties?.name ?? 'Fasilitas publik');
          new maplibregl.Popup({ offset: 10 })
            .setLngLat(coordinates)
            .setHTML(`<strong>${name}</strong><br><small>Data fasilitas demo JakComfort</small>`)
            .addTo(map);
        });

        const style = map.getStyle();
        const buildingLayer = style.layers?.find((layer) => 'source-layer' in layer && layer['source-layer'] === 'building');
        if (buildingLayer && 'source' in buildingLayer && typeof buildingLayer.source === 'string') {
          const firstLabel = style.layers?.find((layer) => layer.type === 'symbol' && layer.layout?.['text-field']);
          map.addLayer({
            id: 'jakcomfort-3d-buildings',
            source: buildingLayer.source,
            'source-layer': 'building',
            type: 'fill-extrusion',
            minzoom: 14,
            paint: {
              'fill-extrusion-color': '#d8e7da',
              'fill-extrusion-height': ['coalesce', ['get', 'render_height'], ['get', 'height'], 8],
              'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], 0],
              'fill-extrusion-opacity': 0.72,
            },
          }, firstLabel?.id);
        }
      } catch (error) {
        console.warn('Sebagian layer demo tidak dapat dimuat:', error);
      }
    });

    return () => {
      markerRefs.current.forEach((marker) => marker.remove());
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    markerRefs.current.forEach((marker) => marker.remove());
    markerRefs.current = locations.map((location) => {
      const element = document.createElement('button');
      element.className = `map-score-marker ${location.id === selectedId ? 'is-selected' : ''}`;
      element.style.background = markerColor(location.comfortScore);
      element.innerHTML = `<span>${location.comfortScore}</span>`;
      element.title = `${location.name}: ${location.comfortScore}`;
      element.addEventListener('click', () => onSelect?.(location));
      return new maplibregl.Marker({ element, anchor: 'bottom' })
        .setLngLat(location.coordinates)
        .addTo(map);
    });
  }, [locations, selectedId, onSelect]);

  useEffect(() => {
    if (!selectedId || !mapRef.current) return;
    const location = locations.find((item) => item.id === selectedId);
    if (!location) return;
    mapRef.current.flyTo({ center: location.coordinates, zoom: 15, pitch: is3d ? 55 : 0, bearing: is3d ? -18 : 0, duration: 1200 });
  }, [selectedId, locations, is3d]);

  const toggle3d = () => {
    const map = mapRef.current;
    if (!map) return;
    const next = !is3d;
    setIs3d(next);
    map.easeTo({ pitch: next ? 48 : 0, bearing: next ? -12 : 0, duration: 700 });
  };

  return (
    <div className={`map-view ${className}`}>
      <div ref={containerRef} className="map-view__canvas" />
      <button type="button" className="map-view__toggle" onClick={toggle3d}>{is3d ? '2D' : '3D'}</button>
      <div className="map-view__legend">
        <span><i className="legend-dot legend-dot--green" /> Nyaman</span>
        <span><i className="legend-dot legend-dot--orange" /> Cukup</span>
        <span><i className="legend-dot legend-dot--red" /> Perlu perhatian</span>
      </div>
    </div>
  );
}
